<template>
  <div class="smart-image" :style="ratioStyle">
    <img
      v-if="!failed"
      class="smart-image__img"
      :src="src"
      :alt="alt"
      :width="width"
      :height="height"
      :loading="eager ? 'eager' : 'lazy'"
      :decoding="eager ? 'auto' : 'async'"
      :fetchpriority="eager ? 'high' : undefined"
      @error="failed = true"
    />
    <div v-else class="smart-image__fallback">
      <span aria-hidden="true">⬚</span>
      <span class="visually-hidden">{{ alt }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

/*
 * loading="lazy" appeared exactly once in the whole source tree, in a Game
 * Master component. No public <img> carried width/height either, so every
 * image shifted layout as it arrived.
 *
 * The previous failure handling set style.display='none', which left a
 * bordered empty frame — and the v-else placeholders next to it could never
 * fire, because they tested whether a src was *present*, not whether it
 * loaded.
 */
const props = defineProps({
  src: { type: String, required: true },
  alt: { type: String, required: true },
  width: { type: [Number, String], default: undefined },
  height: { type: [Number, String], default: undefined },
  ratio: { type: String, default: '16 / 10' },
  eager: Boolean,
});

const failed = ref(false);
const ratioStyle = computed(() => ({ aspectRatio: props.ratio }));
</script>

<style scoped>
.smart-image {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: var(--radius);
  background: var(--surface-1);
}

.smart-image__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.smart-image__fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: var(--text-faint);
  font-size: var(--text-2xl);
  background: var(--surface-1);
}
</style>
